import moment from "moment";
import pxd from "parse-xsd-duration";
import { toast, ToastPosition } from "react-toastify";
import {
  PASSWORD_STRENGTH,
  ENV_MAPPER,
  APP_PERMISSION_MAPPER,
  IATA_LIST,
  SHARED,
  OWNED,
  SUBAGENCY,
  NOLONGER,
  BAGGAGE,
  PENALTY_CODE,
  LI_OWNER_CODE,
  IS_LOCAL_INVENTORY_SUBSCRIPTION,
  TK_OWNER,
  FLIGHT_TAXONOMY_CODE,
} from "utils/constants";
import jwt_decode from "jwt-decode";
import md5 from "md5";
import { IAirportList } from "store/airport";
import { env } from "config/env";

export interface ProxyObjectType {}

// const jwt = require("jsonwebtoken");

const emptyProxyObject: ProxyObjectType = new Proxy(
  {},
  {},
  // {
  //     get: () => {
  //         return emptyProxyObject;
  //     },
  // }
);

const isEmpty = (val: any) => {
  // Stolen From: https://stackoverflow.com/a/28953167
  /*
    test results
    --------------
    [] true, empty array
    {} true, empty object
    null true
    undefined true
    "" true, empty string
    '' true, empty string
    0 false, number
    true false, boolean
    false false, boolean
    Date false
    function false
    */
  if (val === emptyProxyObject) return true;
  if (val === undefined) return true;

  if (
    typeof val == "function" ||
    typeof val == "number" ||
    typeof val == "boolean" ||
    Object.prototype.toString.call(val) === "[object Date]"
  )
    return false;

  if (val == null || val.length === 0)
    // null or 0 length array
    return true;

  if (typeof val == "object") if (Object.keys(val).length === 0) return true;

  return false;
};

export const getValue = (val: any) => {
  return isEmpty(val) ? "-" : val;
};

export interface PasswordStrength {
  length: Boolean;
  numeric: Boolean;
  lowerCase: Boolean;
  upperCase: Boolean;
  specialCharacter: Boolean;
}

export const passwordValidator = (value: any) => {
  const result = {
    length: false,
    lowerCase: false,
    upperCase: false,
    specialCharacter: false,
    numeric: false,
  };
  if (!isEmpty(value)) {
    if (PASSWORD_STRENGTH.MIN_LENGTH <= value.length) {
      result.length = true;
    }
    if (value.match(/[_A-Z]/)) {
      result.upperCase = true;
    }
    if (value.match(/[_a-z]/)) {
      result.lowerCase = true;
    }
    if (value.match(/[_0-9]/)) {
      result.numeric = true;
    }
    if (value.match(/[_\W]/)) {
      result.specialCharacter = true;
    }
  }
  return result;
};

export const notify = (type: string, message: string) => {
  type === "success"
    ? toast.success(message, {
        position: "top-right" as ToastPosition,
        autoClose: 10000,
        theme: "colored",
      })
    : type === "warning"
      ? toast.warning(message, {
          position: "top-right" as ToastPosition,
          autoClose: 10000,
          theme: "colored",
        })
      : toast.error(message, {
          position: "top-right" as ToastPosition,
          autoClose: 10000,
          theme: "colored",
        });
};

const setLocalStorage = (key: string, value: any) => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

const getLocalStorage = (key: string) => {
  const value: any = window.localStorage.getItem(key);
  return !isEmpty(value) ? JSON.parse(value) : null;
};

const clearLocalStorage = (key: Array<any>) => {
  key.forEach((item) => window.localStorage.removeItem(item));
};

const setSessionStorage = (key: string, value: any) => {
  window.sessionStorage.setItem(key, JSON.stringify(value));
};

const getSessionStorage = (key: string) => {
  const value: any = window.sessionStorage.getItem(key);
  return !isEmpty(value) ? JSON.parse(value) : null;
};

const clearSessionStorage = (key: Array<any>) => {
  key.forEach((item) => window.sessionStorage.removeItem(item));
};
const readStringifyObj = (name: string) => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? match.split("=")[1] : null;
};

const decodeReadStringifyObj = (name: string) => {
  const raw = readStringifyObj(name);

  if (raw) {
    try {
      const decoded = decodeURIComponent(raw);
      return JSON.parse(decoded);
    } catch (err) {
      console.error("Failed to parse permissions cookie", err);
      return null;
    }
  }

  return null;
};

const counterValueHandler = (value: number, type: string) => {
  let temp = value;
  if (type === "-" && value !== 0) {
    temp = temp - 1;
  } else if (type === "+") {
    temp = temp + 1;
  }
  return temp;
};

const dateChangeHandler = (value: any, type: string) => {
  let temp = "";
  if (type === "-") {
    temp = moment(value).subtract(1, "days").format();
  } else if (type === "+") {
    temp = moment(value).add(1, "days").format();
  }
  return temp;
};

const buildDateToSQLFmt = (ipDate: any) => {
  return (
    new Date(ipDate).getFullYear() +
    "-" +
    ("0" + (new Date(ipDate).getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + new Date(ipDate).getDate()).slice(-2)
  );
};
const buildDateFromSQLFmt = (ipDate: any) => {
  let year = ipDate?.split("-")[0];
  let month = ipDate?.split("-")[1] - 1;
  let day = ipDate?.split("-")[2];
  return new Date(year, month, day);
};

const compareObjects = (obj1: any, obj2: any) => {
  const obj1Length = Object.keys(obj1).length;
  const obj2Length = Object.keys(obj2).length;
  if (obj1Length === obj2Length) {
    return Object.keys(obj1).every(
      (key) => obj2.hasOwnProperty(key) && obj2[key] === obj1[key],
    );
  }
  return false;
};

// const convertDurationForUI = (duration: any) => {
//   const converted = pxd(duration, true);
//   let convertedFormat = "";

//   if (converted?.days) {
//     convertedFormat += `${converted.days} <span>${
//       converted.days > 1 ? "days" : "day"
//     }</span> `;
//   }
//   if (converted?.hours) {
//     convertedFormat += `${converted.hours} <span>hr</span> `;
//   }
//   if (converted?.minutes) {
//     convertedFormat += `${converted.minutes} <span>mins</span>`;
//   }

//   return convertedFormat.trim();
// };
const convertDurationForUI = (duration: string) => {
  // Parse ISO 8601 duration e.g. "PT26H45M"
  const hoursMatch = duration?.match(/(\d+)H/);
  const minutesMatch = duration?.match(/(\d+)M/);

  const totalHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  let convertedFormat = "";

  if (days > 0) {
    convertedFormat += `${days} <span>${days > 1 ? "days" : "day"}</span> `;
    const totalRemainingMins = remainingHours * 60 + minutes;
    if (remainingHours > 0) {
      convertedFormat += `${remainingHours} <span>hr</span> `; // show hours separately
    }
    if (minutes > 0) {
      convertedFormat += `${minutes} <span>mins</span>`;
    }
  } else {
    if (remainingHours > 0) {
      convertedFormat += `${remainingHours} <span>hr</span> `;
    }
    if (minutes > 0) {
      convertedFormat += `${minutes} <span>mins</span>`;
    }
  }

  return convertedFormat.trim();
};
export const convertDuration = (duration: any) => {
  const converted = pxd(duration, true);
  const convertedFormat = {
    days: converted?.days,
    hours: converted?.hours,
    minutes: converted?.minutes,
  };
  if (converted?.hours > 24) {
    const days = Math.floor(converted?.hours / 24);
    const remainingHours = converted?.hours % 24;
    convertedFormat.days = days;
    convertedFormat.hours = remainingHours;
  }
  return convertedFormat;
};

export const handlePromotionServiceMoreInfoLink = (
  e: any,
  popWidth: number,
  popHeight: number,
  layoverDuration?: any,
) => {
  e.stopPropagation();
  console.log("X:", e.pageX, "Y:", e.pageY);
  const dialogWidth = popWidth;
  const dialogHeight = popHeight;
  const dialogHalfWidth = dialogWidth / 2;
  const dialogPos = {
    left: e.pageX - dialogHalfWidth,
    top: e.pageY - dialogHeight - 15,
    isOpen: true,
    additionClassName: "",
    layoverDuration: layoverDuration ? layoverDuration : null,
  };
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  const extraMargin = 20;
  const rightWindowRemainingSpace = windowWidth - e.pageX;
  if (rightWindowRemainingSpace < dialogHalfWidth) {
    const diffSpace = dialogHalfWidth - rightWindowRemainingSpace;

    dialogPos.left = dialogPos.left - diffSpace - extraMargin;
  }
  if (e.pageY < dialogHeight) {
    dialogPos.top = e.pageY + extraMargin;
    dialogPos.additionClassName = "top-arrow";
  }
  return dialogPos;
};

export const isOfferEligibleForPromotionService = (
  offerPriceData: any,
  ownerCode: string,
) => {
  if (!isEmpty(offerPriceData) && ownerCode === TK_OWNER) {
    const collectAllSegments: any = [];
    for (const item of offerPriceData) {
      const itemPaxSegment = item?.paxSegment;
      for (const segment of itemPaxSegment) {
        collectAllSegments.push(segment);
      }
    }
    if (!isEmpty(collectAllSegments)) {
      const collectArrivalIataLocationCode = collectAllSegments?.map(
        (item: any) => item?.arrival?.iataLocationCode,
      );
      const collectArrivalIataLocationCodeLast =
        collectArrivalIataLocationCode.pop();
      if (collectArrivalIataLocationCode?.some((item: any) => item === "IST")) {
        const findPromoSegmentIndex = collectAllSegments?.findIndex(
          (item: any) => item?.arrival?.iataLocationCode === "IST",
        );
        if (findPromoSegmentIndex !== -1) {
          const promoSegment = collectAllSegments?.[findPromoSegmentIndex];
          const promoSegmentNext =
            collectAllSegments?.[findPromoSegmentIndex + 1];
          let layoverDuration: any = convertDuration(
            moment
              .duration(
                moment(
                  moment(
                    promoSegment?.arrivalTime ||
                      promoSegment?.arrival?.aircraftScheduledDateTime,
                  ),
                ).diff(
                  moment(
                    promoSegmentNext?.departureTime ||
                      promoSegmentNext?.dep?.aircraftScheduledDateTime,
                  ),
                  "seconds",
                ),
                "seconds",
              )
              .toJSON(),
          );
          if (layoverDuration?.days) {
            const daysHours = layoverDuration?.days * 24;
            layoverDuration.days = 0;
            layoverDuration.hours = layoverDuration?.hours + daysHours;
          }
          if (
            (layoverDuration?.hours > 6 && layoverDuration?.hours < 24) ||
            (layoverDuration?.hours === 6 && layoverDuration?.minutes > 0) ||
            (layoverDuration?.hours === 24 && layoverDuration?.minutes <= 0)
          ) {
            // if (
            //   (layoverDuration?.hours > 6 && layoverDuration?.hours <= 24) ||
            //   (layoverDuration?.hours === 6 &&
            //     layoverDuration?.minutes > 0 &&
            //     layoverDuration?.hours <= 24)
            // ) {
            // return true;
            return {
              eligible: true,
              layoverDuration,
            };
          } else {
            // return false;
            return {
              eligible: false,
              layoverDuration: null,
            };
          }
        }
      }
    }
    // const collectArrivalIataLocationCode = offerPriceData?.[0]?.paxSegment?.map(
    //   (item: any) => item?.arrival?.iataLocationCode
    // );
    // const collectArrivalIataLocationCodeLast =
    //   collectArrivalIataLocationCode.pop();

    // if (collectArrivalIataLocationCode?.some((item: any) => item === "IST")) {
    //   const findPromoSegmentIndex = offerPriceData?.[0]?.paxSegment?.findIndex(
    //     (item: any) => item?.arrival?.iataLocationCode === "IST"
    //   );
    //   if (findPromoSegmentIndex !== -1) {
    //     const promoSegment =
    //       offerPriceData?.[0]?.paxSegment?.[findPromoSegmentIndex];
    //     const promoSegmentNext =
    //       offerPriceData?.[0]?.paxSegment?.[findPromoSegmentIndex + 1];
    //     let layoverDuration: any = convertDuration(
    //       moment
    //         .duration(
    //           moment(
    //             moment(
    //               promoSegment?.arrivalTime ||
    //                 promoSegment?.arrival?.aircraftScheduledDateTime
    //             )
    //           ).diff(
    //             moment(
    //               promoSegmentNext?.departureTime ||
    //                 promoSegmentNext?.dep?.aircraftScheduledDateTime
    //             ),
    //             "seconds"
    //           ),
    //           "seconds"
    //         )
    //         .toJSON()
    //     );
    //     if (layoverDuration?.days) {
    //       const daysHours = layoverDuration?.days * 24;
    //       layoverDuration.days = 0;
    //       layoverDuration.hours = layoverDuration?.hours + daysHours;
    //     }
    //     if (
    //       (layoverDuration?.hours > 6 && layoverDuration?.hours <= 24) ||
    //       (layoverDuration?.hours === 6 &&
    //         layoverDuration?.minutes > 0 &&
    //         layoverDuration?.hours <= 24)
    //     ) {
    //       // return true;
    //       return {
    //         eligible: true,
    //         layoverDuration,
    //       };
    //     } else {
    //       // return false;
    //       return {
    //         eligible: false,
    //         layoverDuration: null,
    //       };
    //     }
    //   }
    // }
    if (offerPriceData.length > 1) {
      const collectArrivalIataLocationCode = offerPriceData.map(
        (list: any) =>
          list?.paxSegment?.[list?.paxSegment.length - 1]?.arrival
            ?.iataLocationCode,
      );
      const collectArrivalIataLocationCodeLast =
        collectArrivalIataLocationCode.pop();
      let layoverDuration: any;
      if (collectArrivalIataLocationCode?.some((item: any) => item === "IST")) {
        const checkEligiblePromoServiceJourneyWise: any = [];
        for (const [index, list] of offerPriceData.entries()) {
          if (
            list?.paxSegment?.[list?.paxSegment?.length - 1]?.arrival
              ?.iataLocationCode === "IST"
          ) {
            const promoSegment = list?.paxSegment;
            const lastPromoSegment = promoSegment?.[promoSegment?.length - 1];
            const promoJouneyNextSegment =
              offerPriceData?.[index + 1]?.paxSegment?.[0];
            layoverDuration = convertDuration(
              moment
                .duration(
                  moment(
                    moment(
                      lastPromoSegment?.arrivalTime ||
                        lastPromoSegment?.arrival?.aircraftScheduledDateTime,
                    ),
                  ).diff(
                    moment(
                      promoJouneyNextSegment?.departureTime ||
                        promoJouneyNextSegment?.dep?.aircraftScheduledDateTime,
                    ),
                    "seconds",
                  ),
                  "seconds",
                )
                .toJSON(),
            );
            if (layoverDuration?.days) {
              const daysHours = layoverDuration?.days * 24;
              layoverDuration.days = 0;
              layoverDuration.hours = layoverDuration?.hours + daysHours;
            }
            // if (
            //   layoverDuration?.days < 7 ||
            //   (layoverDuration?.days === 0 && layoverDuration?.hours > 6) ||
            //   (layoverDuration?.days === 0 &&
            //     layoverDuration?.hours === 6 &&
            //     layoverDuration?.minutes > 0)
            // ) {
            if (
              (layoverDuration?.hours > 6 && layoverDuration?.hours < 24) ||
              (layoverDuration?.hours === 6 && layoverDuration?.minutes > 0) ||
              (layoverDuration?.hours === 24 && layoverDuration?.minutes <= 0)
            ) {
              checkEligiblePromoServiceJourneyWise.push(true);
            } else {
              checkEligiblePromoServiceJourneyWise.push(false);
            }
          }
        }
        // console.log(
        //   "checkEligiblePromoServiceJourneyWise",
        //   checkEligiblePromoServiceJourneyWise
        // );
        if (checkEligiblePromoServiceJourneyWise.includes(true)) {
          // return true;
          return {
            eligible: true,
            layoverDuration,
          };
        } else {
          // return false;
          return {
            eligible: false,
            layoverDuration: null,
          };
        }
      }
    }
  } else {
    // return false;
    return {
      eligible: false,
      layoverDuration: null,
    };
  }
};

const deepClone = (data: any) => {
  if (data) {
    return JSON.parse(JSON.stringify(data));
  }
};

const legacyRedirectAppUrl = (host: string, baseUrl: string | undefined) => {
  if (
    baseUrl?.includes("localhost") ||
    baseUrl?.includes("127.0.0.1") ||
    baseUrl?.includes(".dev.")
  ) {
    return `https://${host}.dev.nuflights.com/`;
  }
  if (baseUrl?.includes(".qa.")) {
    return `https://${host}.qa.llc.nuflights.com/`;
  }
  if (baseUrl?.includes(".staging.")) {
    return `https://${host}.staging.llc.nuflights.com/`;
  }
  if (baseUrl?.includes(".intg.")) {
    return `https://${host}.intg.llc.nuflights.com/`;
  }
  if (baseUrl?.includes(".connect.")) {
    let hostShortCode = "";
    if (host === "workbench") {
      hostShortCode = "wb";
    } else if (host === "account") {
      hostShortCode = "ac";
    } else if (host === "inventory") {
      hostShortCode = "inv";
    } else if (host === "platform") {
      hostShortCode = "nf";
    } else {
      hostShortCode = host;
    }
    return `https://${hostShortCode}.connect.nuflights.com/`;
  }
  return `https://${host}.v3.nuflights.com/`;
};

// Pure, parameter-driven so it can be unit tested without mocking
// import.meta.env / window.
//
// `baseUrl` (env.baseUrl / VITE_BASE_URL) holds the *home app's* URL, not
// this app's own URL, so we can't compare it directly against
// currentHostname like a same-app base URL. Instead compare the "tier
// suffix" (everything after each hostname's own leading label) - if they
// match, this is a standard deployment and behavior is identical to the
// legacy tier-based lookup (dev/qa/staging/prod/intg).
//
// If they don't match (e.g. this app is running on an ad hoc subdomain like
// "workbench.new.dev.nuflights.com" while home's configured base URL is
// still the plain "home.dev.nuflights.com"), preserve the extra subdomain
// segment(s) from the live hostname so links back to "home" (and to other
// sibling apps) land on the matching custom subdomain instead of the
// standard shared one.
const suffixAfterFirstLabel = (hostname: string) =>
  hostname.split(".").slice(1).join(".");

const resolveRedirectAppUrl = (
  host: string,
  baseUrl: string | undefined,
  currentHostname: string | undefined
): string => {
  if (!currentHostname) {
    return legacyRedirectAppUrl(host, baseUrl);
  }

  let expectedHostname: string | undefined;
  try {
    expectedHostname = baseUrl ? new URL(baseUrl).hostname : undefined;
  } catch {
    expectedHostname = undefined;
  }

  if (!expectedHostname) {
    return legacyRedirectAppUrl(host, baseUrl);
  }

  const currentSuffix = suffixAfterFirstLabel(currentHostname);
  const expectedSuffix = suffixAfterFirstLabel(expectedHostname);

  if (currentSuffix === expectedSuffix) {
    return legacyRedirectAppUrl(host, baseUrl);
  }

  if (!currentSuffix) {
    return legacyRedirectAppUrl(host, baseUrl);
  }

  return `https://${host}.${currentSuffix}/`;
};

const generateRedirectAppUrl = (host: string) => {
  const baseUrl = env.baseUrl;
  const currentHostname =
    typeof window !== "undefined" ? window.location.hostname : undefined;
  return resolveRedirectAppUrl(host, baseUrl, currentHostname);
};

const getEnv = () => {
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  // console.log("baseUrl", baseUrl);
  if (baseUrl?.includes("localhost") || baseUrl?.includes("127.0.0.1")) {
    return ENV_MAPPER.local;
  }
  if (baseUrl?.includes(".dev.")) {
    return ENV_MAPPER.development;
  }
  if (baseUrl?.includes(".qa.")) {
    return ENV_MAPPER.qa;
  }
  if (baseUrl?.includes(".staging.")) {
    return ENV_MAPPER.staging;
  }
  if (baseUrl?.includes(".intg.")) {
    return ENV_MAPPER.integration;
  }
  if (baseUrl?.includes(".connect.")) {
    return ENV_MAPPER.connect;
  }
  return ENV_MAPPER.production;
};

const verifyAuthToken = (authToken: string, appPermission: string) => {
  let decodedToken: any, scope;
  try {
    decodedToken = jwt_decode(authToken);
    scope = decodedToken?.scope?.split(" ") || [];
  } catch (err) {
    console.error(err);
  }
  return Boolean(scope?.includes(APP_PERMISSION_MAPPER[appPermission]));
};

const convertCurrencyLocale = (currency: any, authToken: string) => {
  let decodedToken: any = jwt_decode(authToken);
  let locale = decodedToken["user_locale"] || "en-GB";
  const formatter = new Intl.NumberFormat(locale, { minimumFractionDigits: 2 });
  return formatter.format(currency);
};

const nodeEnv = env.nodeEnv;
// const generateRedirectUrl = (host : string) =>
//     nodeEnv === "production"
//         ? `https://${host}.nuflights.com/`
//         : `https://${host}.${ENV_MAPPER[nodeEnv] || "dev"}.nuflights.com/`;

const transactionIdMapper = (nfOrderId: any) => {
  const localTransaction = getSessionStorage("transaction") || null;
  if (!isEmpty(nfOrderId) && localTransaction?.nfOrderId === nfOrderId) {
    return localTransaction.id;
  } else if (
    nfOrderId === "airshopping" &&
    !isEmpty(localTransaction?.id) &&
    isEmpty(localTransaction.nfOrderId)
  ) {
    return localTransaction?.id;
  } else {
    clearSessionStorage(["transaction"]);
    return null;
  }
};

const convertPassengerDetails = (data: any) => {
  const passCount: any = {
    ADT: 0,
    CHD: 0,
    INF: 0,
  };
  data?.forEach((pass: any) => {
    passCount[pass.ptc] = passCount[pass.ptc] + 1;
  });
  let showText: any = "";
  if (passCount.ADT !== 0) {
    showText = showText + passCount.ADT + " adult(s)";
  }
  if (passCount.CHD !== 0) {
    showText = showText
      ? `${showText}, ${passCount.CHD} child(s)`
      : `${passCount.CHD} child(s)`;
  }
  if (passCount.INF !== 0) {
    showText = showText
      ? `${showText}, ${passCount.INF} infant(s)`
      : `${passCount.INF} infant(s)`;
  }
  return showText;
};

// const convertPassengerToMd5 = (
//   firstname: string,
//   middlename: string,
//   lastname: string,
//   birth: string,
//   passIndividualHashBy?: string
// ) => {
//   const firstName = firstname ? firstname.trim() : "";
//   const middleName = middlename ? middlename.trim() : "";
//   const lastName = lastname ? lastname.trim() : "";
//   const dob = birth;
//   let birthdate = "";
//   if (!isEmpty(dob)) {
//     const date = new Date(dob);
//     const day =
//       date.getDate().toString().length === 1
//         ? `0${date.getDate().toString()}`
//         : date.getDate().toString();
//     const getMonth: any = date.getMonth();
//     const monthFinal = parseInt(getMonth) + 1;
//     const month =
//       monthFinal.toString().length === 1
//         ? `0${monthFinal.toString()}`
//         : monthFinal.toString();
//     const year = date.getFullYear().toString();
//     birthdate = `${year}${month}${day}`;
//   }
//   let passengerDetail = `${firstName ? firstName.toUpperCase() : ""}${
//     middleName ? middleName.toUpperCase() : ""
//   }${lastName ? lastName.toUpperCase() : ""}${birthdate ? birthdate : ""}`;

//   const passIndividualHashArr = !isEmpty(passIndividualHashBy)
//     ? passIndividualHashBy?.split(":")
//     : [];
//   if (passIndividualHashArr) {
//     if (passIndividualHashArr.length >= 4) {
//       passengerDetail = `${firstName ? firstName.toUpperCase() : ""}${
//         middleName ? middleName.toUpperCase() : ""
//       }${lastName ? lastName.toUpperCase() : ""}${birthdate ? birthdate : ""}`;
//     } else if (passIndividualHashArr.length < 4) {
//       passengerDetail = `${firstName ? firstName.toUpperCase() : ""}${
//         lastName ? lastName.toUpperCase() : ""
//       }${birthdate ? birthdate : ""}`;
//     }
//   }
// }

const convertPassengerToMd5 = (
  firstname: string,
  middlename: string,
  lastname: string,
  birth: string,
  passIndividualHashBy?: string,
) => {
  const firstName = firstname ? firstname.trim() : "";
  const middleName = middlename ? middlename.trim() : "";
  const lastName = lastname ? lastname.trim() : "";
  const dob = birth;
  let birthdate = "";
  if (!isEmpty(dob)) {
    const date = new Date(dob);
    const day =
      date.getDate().toString().length === 1
        ? `0${date.getDate().toString()}`
        : date.getDate().toString();
    const getMonth: any = date.getMonth();
    const monthFinal = parseInt(getMonth) + 1;
    const month =
      monthFinal.toString().length === 1
        ? `0${monthFinal.toString()}`
        : monthFinal.toString();
    const year = date.getFullYear().toString();
    birthdate = `${year}${month}${day}`;
  }

  let passengerDetail = "";

  const passIndividualHashArr = !isEmpty(passIndividualHashBy)
    ? passIndividualHashBy?.split(":")
    : [];

  if (passIndividualHashArr) {
    if (passIndividualHashArr?.includes("FirstName")) {
      passengerDetail = passengerDetail + `${firstName ? firstName : ""}`;
    }

    if (passIndividualHashArr?.includes("MiddleName")) {
      passengerDetail = passengerDetail + `${middleName ? middleName : ""}`;
    }

    if (passIndividualHashArr?.includes("LastName")) {
      passengerDetail = passengerDetail + `${lastName ? lastName : ""}`;
    }

    if (passIndividualHashArr?.includes("DOB")) {
      passengerDetail = passengerDetail + `${birthdate ? birthdate : ""}`;
    }
  }
  return md5(passengerDetail);
};

const getPassengerListByHash = (
  orderPassengerList: any[],
  fulfillmentOrderPassengers: any,
  passengerIndividualHasByAirline?: any,
) => {
  const newPassengerList: any[] = [];
  if (!isEmpty(fulfillmentOrderPassengers)) {
    for (const passenger of fulfillmentOrderPassengers) {
      const findPassengerInOrderData = orderPassengerList.find((list: any) => {
        const { individual } = list;
        const firstName = individual?.givenName[0];
        const middleName = individual?.middleName[0];
        const lastName = individual?.surname;
        const birthdate = individual?.birthdate;
        const passengerDetailInMd5 = convertPassengerToMd5(
          firstName,
          middleName,
          lastName,
          birthdate,
          passengerIndividualHasByAirline,
        );
        if (passenger.node.passengerHash === passengerDetailInMd5) {
          return passenger;
        } else {
          return null;
        }
      });
      if (!isEmpty(findPassengerInOrderData)) {
        newPassengerList.push(findPassengerInOrderData);
      }
    }
  }
  return newPassengerList;
};

const getMissedPassengerListByHash = (
  orderPassengerList: any[],
  fulfillmentOrderPassengers: any,
  passengerIndividualHasByAirline?: any,
) => {
  let newPassengerList: any[] = [];
  const missedPassengerHashList: any[] = [];
  const missedPassengerList: any = [];
  if (!isEmpty(fulfillmentOrderPassengers) && !isEmpty(orderPassengerList)) {
    for (const [index, passenger] of fulfillmentOrderPassengers.entries()) {
      const findPassengerInOrderData = orderPassengerList.find((list: any) => {
        const { individual } = list;
        const firstName = individual?.givenName[0];
        const middleName = individual?.middleName[0];
        const lastName = individual?.surname;
        const birthdate = individual?.birthdate;
        const passengerDetailInMd5 = convertPassengerToMd5(
          firstName,
          middleName,
          lastName,
          birthdate,
          passengerIndividualHasByAirline,
        );
        if (passenger.node.passengerHash === passengerDetailInMd5) {
          return passenger;
        } else {
          return null;
        }
      });
      if (!isEmpty(findPassengerInOrderData)) {
        newPassengerList.push(findPassengerInOrderData);
      } else {
        missedPassengerHashList.push({
          recordIndex: index,
          record: passenger,
        });
      }
    }

    if (
      !isEmpty(newPassengerList) &&
      newPassengerList.length !== orderPassengerList.length
    ) {
      for (const orderPax of orderPassengerList) {
        const findOrderPaxInNewPassengerList = newPassengerList.find(
          (list: any) => list?.paxId === orderPax?.paxId,
        );
        if (isEmpty(findOrderPaxInNewPassengerList)) {
          missedPassengerList.push(orderPax);
        }
      }
    }
  }

  return {
    missedPassengerHashList,
    missedPassengerList,
  };
};

const getOrderBookedType = (order: any) => {
  const lsIataList = getSessionStorage(IATA_LIST) || [];
  if (!isEmpty(lsIataList) && !isEmpty(order)) {
    const findIataRecord = lsIataList.find(
      (list: any) => list?.node?.number === order?.iataProfile?.number,
    );
    if (!isEmpty(findIataRecord)) {
      if (findIataRecord?.node?.isSharedSubscription === true) {
        if (
          findIataRecord?.node?.organization?.ownAgenyId ===
          order?.relativeSeller?.agency?.id
        ) {
          return SHARED;
        } else {
          return SUBAGENCY;
        }
      } else {
        if (
          findIataRecord?.node?.organization?.id ===
          order?.relativeSeller?.agency?.id
        ) {
          return OWNED;
        } else {
          return SUBAGENCY;
        }
      }
    } else {
      return NOLONGER;
    }
  }
};

const orderBookedBy = (order: any, orgId: any) => {
  if (!isEmpty(order?.sharedSubscription?.id)) {
    if (orgId === order?.relativeSeller?.agency?.id) {
      return SHARED;
    } else {
      return SUBAGENCY;
    }
  } else {
    if (orgId === order?.relativeSeller?.agency?.id) {
      return OWNED;
    } else {
      return SUBAGENCY;
    }
  }
};

const ageCalculator = (depDate: any, selectedDate: any) => {
  // if (depDate.search("T") !== -1)
  //   depDate = depDate.split("T")[0] + "T24:00:00";

  // const type = moment(depDate).diff(
  //   moment(new Date(passengerData.dob)),
  //   "milliseconds"
  // );
  // const duration = moment.duration(type);

  // const years = duration.get("y");
  // const months = duration.get("M");
  // const days = duration.get("d");

  const dateOld = new Date(selectedDate).getDate();
  const monthOld = new Date(selectedDate).getMonth();
  const yearOld = new Date(selectedDate).getFullYear();

  const dateNew = new Date(depDate).getDate();
  const monthNew = new Date(depDate).getMonth();
  const yearNew = new Date(depDate).getFullYear();

  var a = moment([yearNew, monthNew, dateNew]);
  var b = moment([yearOld, monthOld, dateOld]);

  var years = a.diff(b, "year");
  b.add(years, "years");

  var months = a.diff(b, "months");
  b.add(months, "months");

  var days = a.diff(b, "days");

  return {
    years,
    months,
    days,
  };
};

const baseFareWithServiceFee = (
  key: any,
  offerItem: any,
  isReshopFlow: boolean = false,
) => {
  // console.log("offerItem", offerItem);
  const finalSupplierBaseFare =
    key === true
      ? Number(
          offerItem.reduce(
            (total: number, offer: any) =>
              total +
              Number(
                offer.farePriceType[0].price.baseAmount.cdata *
                  // (!isReshopFlow ? offer.paxRefId.length : 1)
                  offer.paxRefId.length,
              ) +
              Number(offer.farePriceType[0].price?.fee[0]?.amount?.cdata),
            0.0,
          ),
        ).toFixed(2)
      : Number(
          offerItem.reduce(
            (total: number, offer: any) =>
              total +
              Number(
                offer.farePriceType[0].price.baseAmount.cdata *
                  // (!isReshopFlow ? offer.paxRefId.length : 1)
                  offer.paxRefId.length,
              ),
            0.0,
          ),
        ).toFixed(2);
  return finalSupplierBaseFare;
};

const carrierFlightCodeAndNumber = (
  marketingCarrierCode: string,
  operatingCarrierCode: string,
  flightNumber: string,
) => {
  if (marketingCarrierCode && operatingCarrierCode) {
    if (marketingCarrierCode === operatingCarrierCode) {
      return `${marketingCarrierCode} ${flightNumber}`;
    } else {
      return `${marketingCarrierCode}(${operatingCarrierCode} ${flightNumber})`;
    }
  } else {
    return `${marketingCarrierCode} ${flightNumber}`;
  }
};

const buildPaxTypeMap = (paxList: any[]) => {
  const map: Record<string, string> = {};

  paxList.forEach((pax) => {
    map[pax.paxId] = pax.ptc;
  });

  return map;
};

type GroupedFare = {
  ptc: string;
  paxSegmentRefIds: string[];
  paxRefIds: string[];
  fareDetails: any;
  price: any;
  service: any;
  orderItemId: string;
  offerItemId: String;
};

const splitRxOfferItemsByPTCJourneyWise = (
  rxOfferItems: any[],
  paxTypeMap: Record<string, string>,
): GroupedFare[] => {
  const grouped: GroupedFare[] = [];

  rxOfferItems.forEach((item) => {
    item.fareDetail?.forEach((fareDetails: any) => {
      if (
        fareDetails?.farePriceType?.[0]?.farePriceTypeCode?.toLowerCase() !=
        "net"
      )
        return;
      fareDetails.fareComponent?.forEach((fc: any) => {
        const segmentKey = fc.paxSegmentRefId.join("_");

        fareDetails.paxRefId = fareDetails.paxRefId ?? [];

        fareDetails.paxRefId.forEach((paxId: string) => {
          const ptc = paxTypeMap[paxId];
          if (!ptc) return;

          let group = grouped.find(
            (g) =>
              g.ptc === ptc &&
              g.paxSegmentRefIds.join("_").includes(segmentKey),
          );

          const collectedFareDetailIndx = grouped.findIndex(
            (grpItm: any) =>
              grpItm.ptc == ptc &&
              (item?.orderItemId
                ? grpItm?.orderItemId == item?.orderItemId
                : grpItm?.offerItemId == item?.offerItemId),
          );

          // ✅ CREATE GROUP (CLONE fareDetails)
          if (!group && collectedFareDetailIndx === -1) {
            group = {
              ptc,
              paxSegmentRefIds: [...fc.paxSegmentRefId], // clone
              paxRefIds: [],
              fareDetails: {
                ...fareDetails, // 🔑 clone object
                paxRefId: [], // 🔑 reset pax list per group
              },
              price: item?.price,
              service: item?.service,
              orderItemId: item?.orderItemId,
              offerItemId: item?.offerItemId,
            };
            grouped.push(group);
          }

          if (collectedFareDetailIndx !== -1) {
            grouped[collectedFareDetailIndx]["paxSegmentRefIds"].push(
              ...fc.paxSegmentRefId,
            );
          }

          // ✅ SAFE NOW — does NOT affect offerPriceData
          if (!group?.fareDetails?.paxRefId.includes(paxId)) {
            group?.fareDetails.paxRefId.push(paxId);
          }

          // ✅ group-level paxRefIds
          if (!group?.paxRefIds.includes(paxId)) {
            group?.paxRefIds.push(paxId);
          }
        });
      });
    });
  });

  return grouped;
};
const refactorRxOfferItems = (rxOfferItems: any[], paxList: any[]) => {
  const paxTypeMap = buildPaxTypeMap(paxList);
  return splitRxOfferItemsByPTCJourneyWise(rxOfferItems, paxTypeMap);
};

const combineRefactoredRxOfferItems = (items: any[]) => {
  const result: any[] = [];

  // Group items by PTC
  const ptcGroups: Record<string, any[]> = {};
  items.forEach((item) => {
    if (!ptcGroups[item.ptc]) ptcGroups[item.ptc] = [];
    ptcGroups[item.ptc].push(item);
  });

  Object.entries(ptcGroups).forEach(([ptc, group]) => {
    // ---- fareDetails template ----
    const combinedFareDetails = JSON.parse(
      JSON.stringify(group[0].fareDetails),
    );

    // ---- price template (NEW) ----
    const combinedPrice = JSON.parse(
      JSON.stringify(group[0].fareDetails?.farePriceType?.[0]?.price),
    );

    // reset price values
    combinedPrice.baseAmount.cdata = "0";
    combinedPrice.totalAmount.cdata = "0";
    // combinedPrice.discount.discountAmount.cdata = "0";
    // combinedPrice.fee.forEach((f: any) => (f.amount.cdata = "0"));
    combinedPrice.taxSummary.forEach((ts: any) => {
      ts.totalTaxAmount.cdata = "0";
      ts.tax.forEach((t: any) => (t.amount.cdata = "0"));
    });

    combinedFareDetails.farePriceType?.forEach((fpt: any, idx: number) => {
      if (!fpt.price) return;

      // local accumulators
      let baseAmountSum = 0;
      let totalAmountSum = 0;
      let discountSum = 0;
      let feeSum = 0;
      let totalTaxAmountSum = 0;
      const taxMap: Record<string, any> = {};

      group.forEach((item) => {
        const price = item.fareDetails?.farePriceType?.[idx]?.price;
        if (!price) return;

        baseAmountSum += Number(price?.baseAmount?.cdata ?? 0);
        totalAmountSum += Number(price?.totalAmount?.cdata ?? 0);
        discountSum += Number(price?.discount?.discountAmount?.cdata ?? 0);

        feeSum += (price.fee ?? []).reduce(
          (sum: number, f: any) => sum + Number(f.amount?.cdata ?? 0),
          0,
        );

        price.taxSummary?.forEach((ts: any) => {
          totalTaxAmountSum += Number(ts?.totalTaxAmount?.cdata ?? 0);

          ts.tax?.forEach((t: any) => {
            if (taxMap[t?.taxCode]) {
              taxMap[t.taxCode].amount.cdata =
                Number(taxMap[t?.taxCode]?.amount?.cdata) +
                Number(t.amount?.cdata ?? 0);
            } else {
              taxMap[t.taxCode] = JSON.parse(JSON.stringify(t));
            }
          });
        });
      });

      // assign back to fareDetails price
      fpt.price.baseAmount.cdata = baseAmountSum.toString();
      fpt.price.totalAmount.cdata = totalAmountSum.toString();
      // fpt.price.discount.discountAmount.cdata = discountSum.toString();
      // fpt.price.fee[0].amount.cdata = feeSum.toString();
      fpt.price.taxSummary[0].tax = Object.values(taxMap);
      fpt.price.taxSummary[0].totalTaxAmount.cdata =
        totalTaxAmountSum.toString();

      // ---- also assign to combinedPrice (NEW) ----
      combinedPrice.baseAmount.cdata = baseAmountSum.toString();
      combinedPrice.totalAmount.cdata = totalAmountSum.toString();
      // combinedPrice.discount.discountAmount.cdata = discountSum.toString();
      // combinedPrice.fee[0].amount.cdata = feeSum.toString();
      combinedPrice.taxSummary[0].tax = Object.values(taxMap);
      combinedPrice.taxSummary[0].totalTaxAmount.cdata =
        totalTaxAmountSum.toString();
    });

    result.push({
      ptc,
      paxSegmentRefIds: Array.from(
        new Set(group.flatMap((g) => g.paxSegmentRefIds)),
      ),
      paxRefIds: Array.from(new Set(group.flatMap((g) => g.paxRefIds))),
      fareDetail: [combinedFareDetails],
      price: combinedPrice, // ✅ NEW combined price
      orderItemId: group?.[0]?.orderItemId,
    });
  });

  return result;
};
export const combineDeleteOrderItemByPTC = (orderItems: any) => {
  const paxToPtcMap = buildPaxTypeMap(
    orderItems?.orderReshopRs?.response?.dataLists?.paxList?.pax ||
      orderItems?.orderData?.[0]?.node?.fullfilmentOrderDetails?.response
        ?.dataLists?.paxList?.pax,
  );

  const deleteItems =
    orderItems?.orderReshopRs?.response?.reshopResults?.reshopOffers?.offer?.[0]
      ?.deleteOrderItem ||
    orderItems?.orderData?.[0]?.node?.fullfilmentOrderDetails?.response
      ?.order?.[0]?.orderItem;

  const ptcGroups: Record<string, any[]> = {};

  deleteItems.forEach((item) => {
    const fd = item.priceDifferential?.newPrice?.fareDetail?.[0];
    const paxId = fd?.paxRefId?.[0];
    const ptc = paxToPtcMap[paxId];
    if (!ptc) return;

    if (!ptcGroups[ptc]) ptcGroups[ptc] = [];
    ptcGroups[ptc].push(item);
  });

  return Object.entries(ptcGroups).map(([ptc, group]) => {
    const template = deepClone(group[0]);
    const combinedFareDetail =
      template.priceDifferential.newPrice.fareDetail[0];
    const combinedPrice = template.priceDifferential.newPrice.price;

    // --- Aggregation Totals ---
    const allPaxIds = new Set<string>();
    let grandBaseSum = 0;
    let grandTotalSum = 0;
    let grandTaxSum = 0;
    const taxMap: Record<string, any> = {};

    group.forEach((item) => {
      const fd = item.priceDifferential.newPrice.fareDetail[0];
      const pr = item.priceDifferential.newPrice.price;

      fd.paxRefId?.forEach((id: string) => allPaxIds.add(id));

      grandBaseSum += Number(fd.price.baseAmount.cdata || 0);
      grandTotalSum += Number(fd.price.totalAmount.cdata || 0);

      fd.price.taxSummary?.forEach((ts: any) => {
        grandTaxSum += Number(ts.totalTaxAmount.cdata || 0);
        ts.tax?.forEach((t: any) => {
          if (taxMap[t.taxCode]) {
            taxMap[t.taxCode].amount.cdata = (
              Number(taxMap[t.taxCode].amount.cdata) + Number(t.amount.cdata)
            ).toString();
          } else {
            taxMap[t.taxCode] = deepClone(t);
          }
        });
      });
    });

    const paxCount = allPaxIds.size || 1;

    const unitBase = (grandBaseSum / paxCount).toString();
    const unitTotal = (grandTotalSum / paxCount).toString();
    const unitTaxTotal = (grandTaxSum / paxCount).toString();

    combinedFareDetail.paxRefId = Array.from(allPaxIds);

    combinedFareDetail.price.baseAmount.cdata = unitBase;
    combinedPrice.baseAmount.cdata = unitBase;

    combinedFareDetail.price.totalAmount.cdata = unitTotal;
    combinedPrice.totalAmount.cdata = unitTotal;

    combinedFareDetail.price.taxSummary[0].totalTaxAmount.cdata = unitTaxTotal;
    combinedPrice.taxSummary[0].totalTaxAmount.cdata = unitTaxTotal;

    const unitTaxes = Object.values(taxMap).map((t: any) => {
      const unitTaxObj = deepClone(t);
      unitTaxObj.amount.cdata = (Number(t.amount.cdata) / paxCount).toString();
      return unitTaxObj;
    });

    combinedFareDetail.price.taxSummary[0].tax = unitTaxes;
    combinedPrice.taxSummary[0].tax = unitTaxes;

    template.ptc = ptc;
    return template;
  });
};

// const getCalculatedFareAmounts = (orderItems: any) => {
//   let baseAmount: number = 0;
//   let taxAmount: number = 0;
//   let totalAmount: number = 0;
//   let discountAmount: number = 0;
//   let serviceFeeAmount: number = 0;
//   let paxLength: number = 0;

//   orderItems?.map((orderItems: any) => {
//     if (!isEmpty(orderItems["fareDetail"])) {
//       for (const fareItem of orderItems["fareDetail"]) {
//         if (fareItem?.farePriceType[0]?.farePriceTypeCode === "Sell") {
//           if (!isEmpty(fareItem?.paxRefId)) {
//             if (
//               fareItem?.paxRefId.length &&
//               fareItem?.paxRefId.length === 1 &&
//               fareItem?.paxRefId[0].search(" ") !== -1
//             ) {
//               paxLength = fareItem?.paxRefId[0]
//                 .replace("[", "")
//                 .replace("]", "")
//                 .replaceAll("'", "")
//                 .split(" ").length;
//             } else {
//               paxLength = fareItem?.paxRefId.length;
//             }
//           }
//           baseAmount =
//             baseAmount +
//             Number(
//               fareItem?.farePriceType[0]?.price?.baseAmount?.cdata * paxLength
//             );
//           taxAmount =
//             taxAmount +
//             Number(
//               fareItem?.farePriceType[0]?.price?.taxSummary[0]?.totalTaxAmount
//                 ?.cdata * paxLength
//             );
//           totalAmount = baseAmount + taxAmount;
//           discountAmount = !isEmpty(fareItem?.price?.discount)
//             ? discountAmount +
//               fareItem?.price?.discount?.discountAmount?.cdata * paxLength
//             : !isEmpty(fareItem?.farePriceType[0]?.price?.discount)
//             ? discountAmount +
//               fareItem?.farePriceType[0]?.price?.discount?.discountAmount
//                 ?.cdata *
//                 paxLength
//             : 0;
//           serviceFeeAmount = !isEmpty(fareItem?.price?.fee)
//             ? serviceFeeAmount +
//               fareItem?.price?.fee[0]?.amount?.cdata * paxLength
//             : !isEmpty(fareItem?.farePriceType[0]?.price?.fee)
//             ? serviceFeeAmount +
//               fareItem?.farePriceType[0]?.price?.fee[0]?.amount?.cdata *
//                 paxLength
//             : 0;
//         }
//       }
//     } else {
//       baseAmount = 0;
//       taxAmount = 0;
//       totalAmount = 0;
//       discountAmount = 0;
//       serviceFeeAmount = 0;
//     }
//   });
//   return {
//     baseAmount,
//     taxAmount,
//     totalAmount,
//     discountAmount,
//     serviceFeeAmount,
//   };
// };

/**
 * Calculate discount and service fee for supplier
 * @allOrderItems: all order items from ndc order detail Object
 * @payableOrderItems: remaining order items those need to be paid
 */
const getSupplierPaymentDiscountFees = (
  allOrderItems: any,
  payableOrderItems: any,
) => {
  let discountAmount: number = 0;
  let serviceFeeAmount: number = 0;
  let paxLength: number = 0;

  if (!isEmpty(payableOrderItems) && !isEmpty(allOrderItems)) {
    const allPayableOrderIds = payableOrderItems?.map(
      (payableOrderItem: any) => payableOrderItem?.id,
    );
    if (!isEmpty(allPayableOrderIds)) {
      allOrderItems
        ?.filter((allOrderItem: any) =>
          allPayableOrderIds.includes(
            allOrderItem?.orderItemId || allOrderItem?.offerItemId,
          ),
        )
        ?.map((orderItems: any) => {
          if (!isEmpty(orderItems["fareDetail"])) {
            for (const fareItem of orderItems["fareDetail"]) {
              if (fareItem?.farePriceType[0]?.farePriceTypeCode === "NET") {
                if (!isEmpty(fareItem?.paxRefId)) {
                  if (
                    fareItem?.paxRefId.length &&
                    fareItem?.paxRefId.length === 1 &&
                    fareItem?.paxRefId[0].search(" ") !== -1
                  ) {
                    paxLength = fareItem?.paxRefId[0]
                      .replace("[", "")
                      .replace("]", "")
                      .replaceAll("'", "")
                      .split(" ").length;
                  } else {
                    paxLength = fareItem?.paxRefId.length;
                  }
                }

                discountAmount =
                  !isEmpty(fareItem?.price?.discount) &&
                  fareItem?.price?.discount?.discountAmount
                    ? discountAmount +
                      fareItem?.price?.discount?.discountAmount?.cdata *
                        paxLength
                    : !isEmpty(fareItem?.farePriceType[0]?.price?.discount) &&
                        fareItem?.farePriceType[0]?.price?.discount
                          ?.discountAmount
                      ? discountAmount +
                        fareItem?.farePriceType[0]?.price?.discount
                          ?.discountAmount?.cdata *
                          paxLength
                      : 0;
                serviceFeeAmount = !isEmpty(fareItem?.price?.fee)
                  ? serviceFeeAmount +
                    fareItem?.price?.fee[0]?.amount?.cdata * paxLength
                  : !isEmpty(fareItem?.farePriceType[0]?.price?.fee)
                    ? serviceFeeAmount +
                      fareItem?.farePriceType[0]?.price?.fee[0]?.amount?.cdata *
                        paxLength
                    : 0;
              }
            }
          }
        });
    }
  }

  return {
    discountAmount,
    serviceFeeAmount,
  };
};

const getDifferenceInArrivalAndDepartureDays = (
  arrivalDate: any,
  departureDate: any,
) => {
  const newArrivalDate: any = new Date(arrivalDate);
  const newDepartureDate: any = new Date(departureDate);
  const arrivalSplit = moment([
    newArrivalDate.getFullYear(),
    newArrivalDate.getMonth(),
    newArrivalDate.getDate(),
  ]);
  const departureSplit = moment([
    newDepartureDate.getFullYear(),
    newDepartureDate.getMonth(),
    newDepartureDate.getDate(),
  ]);
  return arrivalSplit.diff(departureSplit, "days");
};

const collectOfferPreBaggageDetails = (
  offerBaggageList: any,
  dataBaggageAllowanceList: any,
  dataPaxList: any = [],
  dataPaxJourneyList: any = [],
  listFilterBy: string = "",
  paxsegmentList: any,
) => {
  if (
    !isEmpty(offerBaggageList) &&
    !isEmpty(dataBaggageAllowanceList?.baggageAllowance)
  ) {
    const baggageAllowanceList = dataBaggageAllowanceList?.baggageAllowance;
    const baggageAllowances: any[] = [];
    for (const offerBaggage of offerBaggageList) {
      const findBaggageAllowanceObjectData = baggageAllowanceList.find(
        (item: any) => {
          if (
            item.baggageAllowanceId ===
            (offerBaggage?.baggageAllowanceId ||
              offerBaggage?.baggageAllowanceRefId?.[0])
          ) {
            if (
              (!isEmpty(item?.weightAllowance) &&
                item?.weightAllowance?.[0]?.maximumWeightMeasure !== "0") ||
              (!isEmpty(item?.pieceAllowance) &&
                item?.pieceAllowance?.totalQty !== "0") ||
              (!isEmpty(item?.weightAllowance) &&
                item?.weightAllowance?.[0]?.maximumWeightMeasure !== "0" &&
                !isEmpty(item?.pieceAllowance) &&
                item?.pieceAllowance?.totalQty !== "0")
            ) {
              return item;
            }
          }

          // (item.baggageAllowanceId ===
          //   (offerBaggage?.baggageAllowanceId ||
          //     offerBaggage?.baggageAllowanceRefId?.[0]) &&
          //   //   offerBaggageList?.[0].baggageAllowanceRefId?.[0]) &&
          //   ((!isEmpty(item?.weightAllowance) &&
          //     item?.weightAllowance?.[0]?.maximumWeightMeasure !== "0") ||
          //     (!isEmpty(item?.pieceAllowance) &&
          //       item?.pieceAllowance?.totalQty !== "0"))) ||
          //   (!isEmpty(item?.weightAllowance) &&
          //     item?.weightAllowance?.[0]?.maximumWeightMeasure !== "0" &&
          //     !isEmpty(item?.pieceAllowance) &&
          //     item?.pieceAllowance?.totalQty !== "0");
        },
      );
      if (!isEmpty(findBaggageAllowanceObjectData)) {
        const findBaggageAllowance = deepClone(findBaggageAllowanceObjectData);
        // offerBaggage["descText"] = ["$PaxRefID$:T1 T2", "$PaxJourneyRefID$:Ifltruxbp1fdsfvz9"];
        const offerFlightAssociations = offerBaggage?.offerFlightAssociations;
        const offerPaxSegmentRefId =
          offerFlightAssociations?.paxSegmentReferences?.paxSegmentRefId;

        if (
          !isEmpty(offerBaggage?.descText) ||
          !isEmpty(offerBaggage?.paxRefId?.[0])
        ) {
          const findDescPaxRefIdText = offerBaggage?.descText?.find(
            (desc: any) => desc.search("PaxRefID") !== -1,
          );
          if (
            (!isEmpty(findDescPaxRefIdText) ||
              !isEmpty(offerBaggage?.paxRefId?.[0])) &&
            !isEmpty(dataPaxList)
          ) {
            const paxRefIdArray = findDescPaxRefIdText?.split(":");
            const paxIdContent =
              !isEmpty(paxRefIdArray) && paxRefIdArray.length > 0
                ? paxRefIdArray[1]
                : "";
            // console.log("offerBaggage?.paxRefId", offerBaggage?.paxRefId);
            const paxIds = !isEmpty(paxIdContent)
              ? paxIdContent.split(" ")
              : offerBaggage?.paxRefId;
            const collectedPtc: any[] = [];
            if (!isEmpty(paxIds)) {
              for (const paxId of paxIds) {
                const findPtc = dataPaxList.find(
                  (list: any) => list.paxId === paxId,
                );
                if (!isEmpty(findPtc)) {
                  if (!collectedPtc.includes(findPtc?.ptc)) {
                    collectedPtc.push(deepClone(findPtc?.ptc));
                  }
                }
              }
            }
            findBaggageAllowance["eligiblePtc"] = collectedPtc;
          }
          const findDescPaxJourneyRefIdText = offerBaggage?.descText?.find(
            (desc: any) => desc.search("PaxJourneyRefID") !== -1,
          );

          let paxJourneyIds: any[] = [];
          paxsegmentList.map((paxsegment: any) => {
            paxJourneyIds.push(paxsegment?.paxJourneyRefId);
          });
          if (
            (!isEmpty(findDescPaxJourneyRefIdText) ||
              !isEmpty(
                // offerBaggage?.offerFlightAssociations?.paxJourneyRef
                //   ?.paxJourneyRefId
                //   paxsegment?.paxJourneyRefId
                paxJourneyIds,
                //   journeyList.journeyPriceClass[0].paxJourneyRefId
              )) &&
            !isEmpty(dataPaxJourneyList)
          ) {
            const paxJourneyRefIdArray =
              findDescPaxJourneyRefIdText?.split(":");
            const paxJourneyIdContent =
              !isEmpty(paxJourneyRefIdArray) && paxJourneyRefIdArray.length > 0
                ? paxJourneyRefIdArray[1]
                : "";
            const paxJourneyRefIds = !isEmpty(paxJourneyIdContent)
              ? paxJourneyIdContent?.split(" ")
              : !isEmpty(
                    // paxsegment?.paxJourneyRefId
                    paxJourneyIds,
                    // offerBaggage?.offerFlightAssociations?.paxJourneyRef
                    //   ?.paxJourneyRefId
                    //   journeyList?.journeyPriceClass?.[0]?.paxJourneyRefId
                  )
                ? // ? offerBaggage?.offerFlightAssociations?.paxJourneyRef
                  //     ?.paxJourneyRefId
                  //     paxsegment?.paxJourneyRefId
                  paxJourneyIds
                : [];
            let collectedSegmentIds: any[] = [];
            if (!isEmpty(paxJourneyRefIds)) {
              for (const paxJourneyRefId of paxJourneyRefIds) {
                const findJourney = dataPaxJourneyList.find(
                  // (list: any) => list.paxJourneyId === paxJourneyRefId
                  (list: any) => list.paxJourneyId === paxJourneyRefId,
                );
                if (
                  !isEmpty(findJourney) &&
                  !isEmpty(findJourney?.paxSegmentRefId)
                ) {
                  for (const segRefId of deepClone(
                    findJourney?.paxSegmentRefId,
                  )) {
                    collectedSegmentIds.push(segRefId);
                  }
                }
              }
            }
            if (!isEmpty(offerPaxSegmentRefId)) {
              findBaggageAllowance["paxSegmentRefIds"] = offerPaxSegmentRefId;
            } else {
              findBaggageAllowance["paxSegmentRefIds"] = collectedSegmentIds;
            }
          }
          // })
        }
        baggageAllowances.push(findBaggageAllowance);
      }
    }
    // let uniq: any = {};
    // let filterUniq: any[] = [];
    // if (!isEmpty(baggageAllowances)) {
    //   filterUniq = baggageAllowances.filter(
    //     (obj: any) => !uniq[obj.eligiblePtc] && (uniq[obj.eligiblePtc] = true)
    //   );
    //   return filterUniq;
    // } else {
    //   return [];
    // }
    if (!isEmpty(baggageAllowances)) {
      let uniq: any = {};
      let filterUniq: any[] = [];
      if (listFilterBy === "SEG") {
        const baggageAllowanceDiffBySeg: any[] = [];
        for (const allowance of baggageAllowances) {
          if (!isEmpty(allowance?.paxSegmentRefIds)) {
            for (const segRefId of allowance?.paxSegmentRefIds) {
              const newAllowance = deepClone(allowance);
              newAllowance["paxSegmentRefIds"] = [segRefId];
              baggageAllowanceDiffBySeg.push(newAllowance);
            }
          }
        }
        // console.log("baggageAllowanceDiffBySeg", baggageAllowanceDiffBySeg);
        filterUniq = baggageAllowanceDiffBySeg.filter((obj: any) => {
          return (
            !uniq[
              obj.eligiblePtc + "" + obj.paxSegmentRefIds + "" + obj.typeCode
            ] &&
            (uniq[
              obj.eligiblePtc + "" + obj.paxSegmentRefIds + "" + obj.typeCode
            ] = true)
          );
        });
      } else {
        filterUniq = baggageAllowances.filter(
          (obj: any) =>
            !uniq[obj.eligiblePtc] && (uniq[obj.eligiblePtc] = true),
        );
      }
      if (!isEmpty(filterUniq)) {
        return filterUniq;
      } else {
        return baggageAllowances;
      }
    } else {
      return baggageAllowances;
    }
  } else {
    return [];
  }
};

const collectOfferPreBaggageDetailsByService = (
  orderItems: any,
  dataList: any,
  listFilterBy: string = "",
) => {
  if (
    !isEmpty(orderItems) &&
    !isEmpty(dataList?.baggageAllowanceList?.baggageAllowance) &&
    !isEmpty(dataList?.serviceDefinitionList?.serviceDefinition)
  ) {
    const baggageAllowanceList =
      dataList?.baggageAllowanceList?.baggageAllowance;
    const serviceDefinitionList =
      dataList?.serviceDefinitionList?.serviceDefinition?.filter(
        (item: any) => !isEmpty(item),
      );
    const dataPaxList = dataList?.paxList?.pax;
    const baggageAllowances: any[] = [];
    for (const orderItem of orderItems) {
      if (!isEmpty(orderItem?.service)) {
        for (const orderItemService of orderItem?.service) {
          // if (orderItemService?.descText === BAGGAGE) {
          const serviceDefinitionRefId =
            orderItemService?.orderServiceAssociation?.serviceDefinitionRef
              ?.serviceDefinitionRefId;
          if (serviceDefinitionRefId) {
            const findServiceDefinationId = serviceDefinitionList.find(
              (list: any) =>
                list.serviceDefinitionId === serviceDefinitionRefId,
            );
            if (findServiceDefinationId) {
              const baggageAllowanceRefId =
                findServiceDefinationId?.serviceDefinitionAssociation
                  ?.baggageAllowanceRef?.baggageAllowanceRefId;
              if (baggageAllowanceRefId) {
                // const findBaggageAllowanceObjectData =
                //   baggageAllowanceList.find(
                //     (list: any) =>
                //       list?.baggageAllowanceId ===
                //         baggageAllowanceRefId &&
                //         (!isEmpty(list?.weightAllowance) ||
                //           !isEmpty(list?.pieceAllowance)) ||
                //       (!isEmpty(list?.weightAllowance) &&
                //         !isEmpty(list?.pieceAllowance))
                //   );
                const findBaggageAllowanceObjectData =
                  baggageAllowanceList.find(
                    (list: any) =>
                      list?.baggageAllowanceId === baggageAllowanceRefId[0] &&
                      ((!isEmpty(list?.weightAllowance) &&
                        list?.weightAllowance?.[0]?.maximumWeightMeasure !==
                          "0") ||
                        (!isEmpty(list?.pieceAllowance) &&
                          list?.pieceAllowance?.totalQty !== "0") ||
                        (!isEmpty(list?.weightAllowance) &&
                          list?.weightAllowance?.[0]?.maximumWeightMeasure !==
                            "0" &&
                          !isEmpty(list?.pieceAllowance) &&
                          list?.pieceAllowance?.totalQty !== "0")),
                  );
                if (!isEmpty(findBaggageAllowanceObjectData)) {
                  const findBaggageAllowanceObject = deepClone(
                    findBaggageAllowanceObjectData,
                  );
                  var segmentRefIds = orderItemService?.serviceId;
                  var splitSegment = segmentRefIds.split("_");
                  var paxsegmentRefId = splitSegment[0];
                  findBaggageAllowanceObject["paxSegmentRefId"] =
                    orderItemService?.orderServiceAssociation?.serviceDefinitionRef?.orderFlightAssociations?.paxSegmentRef?.paxSegmentRefId;
                  // paxsegmentRefId;
                  // orderItemService?.orderServiceAssociation?.paxSegmentRef?.paxSegmentRefId;
                  findBaggageAllowanceObject["eligiblePtc"] = dataPaxList?.find(
                    (list: any) => list.paxId === orderItemService?.paxRefId,
                  )?.ptc;
                  baggageAllowances.push(findBaggageAllowanceObject);
                }
              }
            }
          }
          // }
        }
      }
    }

    // Sorting in the order, ADT , CHD, INF
    const ptcOrder: any = { ADT: 0, CHD: 1, INF: 2 };
    baggageAllowances.sort(
      (a: any, b: any) =>
        (ptcOrder[a.eligiblePtc] ?? 99) - (ptcOrder[b.eligiblePtc] ?? 99),
    );

    if (!isEmpty(baggageAllowances)) {
      let uniq: any = {};
      let filterUniq: any[] = [];
      if (listFilterBy === "SEG") {
        filterUniq = baggageAllowances.filter(
          (obj: any) =>
            !uniq[
              obj.eligiblePtc + "" + obj.paxSegmentRefId + "" + obj.typeCode
            ] &&
            (uniq[
              obj.eligiblePtc + "" + obj.paxSegmentRefId + "" + obj.typeCode
            ] = true),
        );
      } else {
        filterUniq = baggageAllowances.filter(
          (obj: any) =>
            !uniq[obj.eligiblePtc] && (uniq[obj.eligiblePtc] = true),
        );
      }
      if (!isEmpty(filterUniq)) {
        return filterUniq;
      } else {
        return baggageAllowances;
      }
    } else {
      return baggageAllowances;
    }
  } else {
    return [];
  }
};
const airlineStandardCommission = (commissionArray: any) => {
  const percentage = commissionArray[0]?.percentage;
  const currencyCode = commissionArray[0]?.amount?.curCode;

  const commission = !isEmpty(commissionArray[0]?.amount?.cdata)
    ? Number(commissionArray[0]?.amount?.cdata)
    : null;
  return {
    percentage,
    currencyCode,
    commission,
  };
};


// Looks up the CONFIRMED/CANCELLED status of a single pax segment by scanning
// every order.orderItem[].service[] entry for the flight ("Right to fly",
// airlineTaxonomy taxonomyCode "0064") service tied to that segment.
// Falls back to CONFIRMED when no matching flight service is found (e.g. WAITLIST
// or any other status is not yet handled and should not be treated as cancelled).
const getPaxSegmentServiceStatus = (
  paxSegmentId: string,
  orderData: any,
): "CONFIRMED" | "CANCELLED" => {
  const orderItems =
    orderData?.[0]?.node?.fullfilmentOrderDetails?.response?.order?.[0]
      ?.orderItem || [];
  const flightService = orderItems
    .flatMap((orderItem: any) => orderItem?.service || [])
    .find((service: any) => {
      const isFlightService = service?.airlineTaxonomy?.some(
        (taxonomy: any) => taxonomy?.taxonomyCode === "0064",
      );
      const segmentRefId =
        service?.orderServiceAssociation?.serviceDefinitionRef
          ?.orderFlightAssociations?.paxSegmentRef?.paxSegmentRefId;
      return isFlightService && segmentRefId === paxSegmentId;
    });
  return flightService?.statusCode === "CANCELLED" ? "CANCELLED" : "CONFIRMED";
};

const getCurrentIataDetails = () => {
  if (window.sessionStorage) {
    const sessionIataList: any = window.sessionStorage.getItem(IATA_LIST) || [];
    if (!isEmpty(sessionIataList)) {
      const iataList = JSON.parse(sessionIataList);
      const defaultIataId: any = window.sessionStorage.getItem("defaultIataID");
      const activeIataRecord =
        !isEmpty(iataList) && !isEmpty(defaultIataId)
          ? iataList.find((list: any) => list.node.id === defaultIataId)
          : null;
      if (!isEmpty(activeIataRecord)) {
        return activeIataRecord?.node;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } else {
    return null;
  }
};

const getPenaltyAmount = (orderItems: any) => {
  let penaltyAmount: any = "0.00";
  if (!isEmpty(orderItems)) {
    const collectPenaltyOrders: any = [];
    for (const orderItem of orderItems) {
      if (!isEmpty(orderItem["service"])) {
        const findPenaltyOrder = orderItem["service"].find(
          (serv: any) => serv?.descText === PENALTY_CODE,
        );
        if (!isEmpty(findPenaltyOrder)) {
          collectPenaltyOrders.push(orderItem);
        }
      }
    }
    if (!isEmpty(collectPenaltyOrders)) {
      penaltyAmount = Number(
        collectPenaltyOrders.reduce(
          (total: number, order: any) =>
            total + Number(order?.price?.totalAmount?.cdata),
          0.0,
        ),
      ).toFixed(2);
    } else {
      penaltyAmount = "0.00";
    }
  }
  return penaltyAmount;
};

const getTotalFareWithDisServ = (order: any, hasDueAmount?: any) => {
  let totalFare: any;
  if (hasDueAmount) {
    totalFare = order?.[0]?.orderItem
      ?.filter(
        // filter out the penalty related order items in total fare calc
        (item: any) =>
          !item?.service?.some((srv: any) => srv?.descText === PENALTY_CODE),
      )
      ?.reduce(
        (sum: number, currentValue: any) =>
          sum +
          Number(
            currentValue?.priceDifferential?.diffPrice?.price?.totalAmount
              ?.cdata ?? 0,
          ),
        0,
      );
  } else {
    totalFare = order?.[0]?.orderItem
      ?.filter(
        // filter out the penalty related order items in total fare calc
        (item: any) =>
          !item?.service?.some((srv: any) => srv?.descText === PENALTY_CODE),
      )
      ?.reduce(
        (sum: number, currentValue: any) =>
          sum + Number(currentValue?.price?.totalAmount?.cdata ?? 0),
        0,
      );
  }
  return totalFare;
};

const removeDuplicates = (array: any) => {
  const uniqueArray: any = [];
  array.forEach(
    (item: any) => uniqueArray.indexOf(item) === -1 && uniqueArray.push(item),
  );
  return uniqueArray;
};

const isSubset = (arr1: Array<any>, arr2: Array<any>): Boolean => {
  for (var i = 0; i < arr1.length; i++) {
    if (arr2.indexOf(arr1[i]) === -1) {
      return false;
    }
  }
  return true;
};

const hasCommonElement = (arr1: Array<any>, arr2: Array<any>): Boolean => {
  for (var i = 0; i < arr1.length; i++) {
    if (arr2.indexOf(arr1[i]) !== -1) {
      return true;
    }
  }
  return false;
};

const serviceConfigValue = (
  ownerCode: string,
  serviceName: string,
  airlineServiceConfigs: any,
) => {
  const findServiceConfigItem =
    !isEmpty(airlineServiceConfigs) &&
    airlineServiceConfigs?.find(
      (config: any) => config.ownerCode === ownerCode,
    );
  if (!isEmpty(findServiceConfigItem)) {
    const findService = findServiceConfigItem?.serviceConfigs?.find(
      (item: any) => item?.keyText === serviceName,
    );
    if (!isEmpty(findService)) {
      return findService?.replacedValueText;
    } else {
      return null;
    }
  }
};

const ownerCodeValue = (ownerCode: string, airlineServiceConfigs: any) => {
  if (!isEmpty(airlineServiceConfigs)) {
    const isLocalInventory = getSessionStorage(IS_LOCAL_INVENTORY_SUBSCRIPTION);
    // const firstAirlineOwnerCode = airlineServiceConfigs?.[0]?.ownerCode;
    const airlineOwnerCode =
      isLocalInventory === null
        ? ownerCode
        : isLocalInventory === true
          ? LI_OWNER_CODE
          : ownerCode;
    if (airlineOwnerCode === LI_OWNER_CODE) {
      return LI_OWNER_CODE;
    } else {
      return ownerCode;
    }
  } else {
    return ownerCode;
  }
};

export const airlinePenaltyAmountByPenaltyRefId = (
  offerItem: any, // orderItems also can be passed
  penaltyList: any,
) => {
  // offerItems can be from offer or order(orderItems)
  // penalties will be collected from fareDetail if not found collect from at orderItem level
  const fareDetail = offerItem?.map(
    (item: any) =>
      item?.reshopPrice?.priceDifferential?.diffPrice?.fareDetail ||
      item?.priceDifferential?.diffPrice?.fareDetail,
  );
  let penaltyRefIds: any = [];

  if (
    !isEmpty(fareDetail) &&
    !fareDetail?.every((itm: any) => itm === undefined) &&
    !isEmpty(penaltyList)
  ) {
    for (const fareDetailItms of fareDetail || []) {
      for (const item of fareDetailItms || []) {
        if (!isEmpty(item?.penaltyRefId)) {
          // penaltyRefIds = [...penaltyRefIds, ...item?.penaltyRefId];
          penaltyRefIds.push(...item?.penaltyRefId);
        }
      }
    }
  }

  if (isEmpty(penaltyRefIds)) {
    // pick from orderItems
    offerItem?.forEach((offItm: any) => {
      if (!isEmpty(offItm?.penaltyRefId)) {
        penaltyRefIds.push(...(offItm?.penaltyRefId || []));
      } else {
        if (isEmpty(offItm?.priceDifferential)) return;
        offItm?.priceDifferential?.diffPrice?.fareDetail?.forEach((fd: any) =>
          penaltyRefIds.push(...(fd?.penaltyRefId || [])),
        );
      }
    });
  }

  if (!isEmpty(penaltyRefIds)) {
    const collectPenaltyRecords: any = [];
    for (const refId of penaltyRefIds) {
      const penaltyRecord = penaltyList?.find(
        (list: any) => list?.penaltyId === refId,
        // (list: any) => list?.penaltyId === refId && list.typeCode === "Change"
      );
      if (!isEmpty(penaltyRecord)) {
        collectPenaltyRecords.push(penaltyRecord);
      }
    }
    const totalPenaltyAmount = collectPenaltyRecords?.reduce(
      (total: any, penalty: any) =>
        total + Number(penalty?.price?.totalAmount?.cdata),
      0.0,
    );
    return totalPenaltyAmount || 0;
  }
  return 0;
};

const ifIncludedRemoveOrAdd = (Array: Array<any>, index: any) => {
  const tempArray = !isEmpty(Array) ? deepClone(Array) : [];
  tempArray?.includes(index)
    ? tempArray.splice(tempArray.indexOf(index), 1)
    : tempArray.push(index);
  return tempArray;
};

const constructPaxListRequest = (paxList: any) => {
  if (!isEmpty(paxList)) {
    const collectPaxRecords: any = [];
    for (const list of paxList) {
      const obj = {
        paxId: list?.paxId,
        ptc: list?.ptc,
        individual: {
          birthdate: list?.individual?.birthdate,
          genderCode: list?.individual?.genderCode,
          titleName: list?.individual?.titleName,
          givenName: list?.individual?.givenName,
          surname: list?.individual?.surname,
          individualId: list?.paxId,
        },
        identityDoc: [
          {
            identityDocTypeCode: list?.identityDoc?.[0]?.identityDocTypeCode,
            identityDocId: list?.identityDoc?.[0]?.identityDocId,
            issuingCountryCode: list?.identityDoc?.[0]?.issuingCountryCode,
            residenceCountryCode: list?.identityDoc?.[0]?.issuingCountryCode,
            surname: list?.identityDoc?.[0]?.surname,
            expiryDate: list?.identityDoc?.[0]?.expiryDate,
          },
        ],
        langUsage: [
          {
            langCode: "EN",
          },
        ],
      };
      if (isEmpty(obj?.identityDoc?.[0]?.surname)) {
        obj.identityDoc = [];
      }
      collectPaxRecords.push(obj);
    }
    return collectPaxRecords;
  } else {
    return [];
  }
};

const getFlownSegmentsFromTicketObject = (ticketEdges: any) => {
  const flownSegmentsFromTicketObject: any = [];
  if (!isEmpty(ticketEdges)) {
    for (const ticket of ticketEdges) {
      const ticketCouponsEdges = ticket?.node?.ticketingCoupons?.edges;
      if (!isEmpty(ticketCouponsEdges)) {
        for (const tCoupon of ticketCouponsEdges) {
          if (
            tCoupon?.node?.status === "B" &&
            !flownSegmentsFromTicketObject.includes(tCoupon?.node?.segmentRef)
          ) {
            flownSegmentsFromTicketObject.push(tCoupon?.node?.segmentRef);
          }
        }
      }
    }
  }
  return flownSegmentsFromTicketObject;
};

// Image Grayscale filter
// const grayScaleCanvas = (ctxImageData: any) => {
//   const data = ctxImageData;
//   for (let i = 0; i < data.length; i += 4) {
//     const gray = 0.3 * data[i] + 0.59 * data[i + 1] + 0.11 * data[i + 2];
//     data[i] = data[i + 1] = data[i + 2] = gray;
//   }
//   return data;
// };
const grayScaleCanvas = (ctxImageData: any, threshold = 128) => {
  const data = ctxImageData;
  for (let i = 0; i < data.length; i += 4) {
    const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
    const value = avg < threshold ? 0 : 255;
    data[i] = data[i + 1] = data[i + 2] = value;
  }
  return data;
};
const clamp = (value: number) => {
  return Math.max(0, Math.min(255, value));
};
// Image Brightness and Contrast filter
const brightnessContrastCanvas = (ctxImageData: any) => {
  const data = ctxImageData;
  const brightness = 30; // range [-255, 255]
  const contrast = 40; // range [-255, 255]
  const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));

  // for (let i = 0; i < data.length; i += 4) {
  //   data[i] = factor * (data[i] - 128) + 128 + brightness;
  //   data[i + 1] = factor * (data[i + 1] - 128) + 128 + brightness;
  //   data[i + 2] = factor * (data[i + 2] - 128) + 128 + brightness;
  // }
  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp(factor * (data[i] - 128) + 128 + brightness);
    data[i + 1] = clamp(factor * (data[i + 1] - 128) + 128 + brightness);
    data[i + 2] = clamp(factor * (data[i + 2] - 128) + 128 + brightness);
  }
  return data;
};

// Image canvas sharpen filter
const sharpenCanvas = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
) => {
  const weights = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  const side = Math.round(Math.sqrt(weights.length));
  const half = Math.floor(side / 2);

  const srcData = ctx.getImageData(0, 0, width, height);
  const output = ctx.createImageData(width, height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0,
        g = 0,
        b = 0;
      for (let cy = 0; cy < side; cy++) {
        for (let cx = 0; cx < side; cx++) {
          const scy = y + cy - half;
          const scx = x + cx - half;

          if (scx >= 0 && scx < width && scy >= 0 && scy < height) {
            const srcOffset = (scy * width + scx) * 4;
            const wt = weights[cy * side + cx];

            r += srcData.data[srcOffset] * wt;
            g += srcData.data[srcOffset + 1] * wt;
            b += srcData.data[srcOffset + 2] * wt;
          }
        }
      }

      const destOffset = (y * width + x) * 4;
      output.data[destOffset] = Math.min(255, Math.max(0, r));
      output.data[destOffset + 1] = Math.min(255, Math.max(0, g));
      output.data[destOffset + 2] = Math.min(255, Math.max(0, b));
      output.data[destOffset + 3] = 255;
    }
  }

  ctx.putImageData(output, 0, 0);
};

const parseMRZDate = (mrzDate: string): string | null => {
  if (!/^\d{6}$/.test(mrzDate)) return null;

  const year = parseInt(mrzDate.slice(0, 2), 10);
  const currentYear = new Date().getFullYear() % 100;

  // Determine the full year
  const fullYear = year > currentYear ? 1900 + year : 2000 + year;
  const fullDateStr = `${fullYear}${mrzDate.slice(2)}`; // YYYYMMDD
  const formatted = moment(fullDateStr, "YYYYMMDD");
  return formatted.isValid() ? formatted.format("YYYY-MM-DD") : null;
};

const convertDataUrlToFile = (
  dataUrl: string,
  filename: string,
): File | undefined => {
  const arr = dataUrl.split(",");
  if (arr.length < 2) {
    return undefined;
  }
  const mimeArr = arr[0].match(/:(.*?);/);
  if (!mimeArr || mimeArr.length < 2) {
    return undefined;
  }
  const mime = mimeArr[1];
  const buff = Buffer.from(arr[1], "base64");
  return new File([buff], filename, { type: mime });
};
const convertDataUrlToFileBinary = (
  dataUrl: string,
  filename?: string,
): File | undefined => {
  const parts = dataUrl.split(",");
  if (parts.length < 2) return undefined;

  // Extract MIME type (image/png, image/jpeg, etc.)
  const mimeMatch = parts[0].match(/:(.*?);/);
  if (!mimeMatch) return undefined;
  const mime = mimeMatch[1];

  // Decode Base64 yobinary
  const bstr = atob(parts[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  // Fix filename extension to match MIME
  const extension = mime.split("/")[1]; // png / jpeg / webp
  const baseName = filename ? filename.replace(/\.[^.]+$/, "") : "image";
  const finalName = `${baseName}.${extension}`;

  //  Return binary File object
  return new File([u8arr], finalName, { type: mime });
};
const carrierLogoLogic = (
  marketingCarrierCode: string,
  operatingCarrierCode: string,
) => {
  if (marketingCarrierCode && operatingCarrierCode) {
    if (marketingCarrierCode === operatingCarrierCode) {
      return {
        isCodeshareFlight: false,
        primary: marketingCarrierCode,
        secondary: null,
      };
    } else {
      return {
        isCodeshareFlight: true,
        primary: marketingCarrierCode,
        secondary: operatingCarrierCode,
      };
    }
  } else {
    return {
      isCodeshareFlight: false,
      primary: marketingCarrierCode,
      secondary: null,
    };
  }
};

const combineFareTypeOrderItemsAndPax = (
  orderItems: any[],
  paxList: any[],
): any[] => {
  const paxTypeMap: Record<string, string> = {};
  paxList.forEach((pax) => {
    paxTypeMap[pax?.paxId] = pax.ptc;
  });
  const groups: Record<string, any[]> = {};
  // -------- GROUP BY TYPE + PTC --------
  orderItems.forEach((item) => {
    const type = item?.type;

    const paxRef = Array.isArray(item?.passengerRef)
      ? item?.passengerRef?.[0]
      : item?.passengerRef;

    const ptc = paxTypeMap[paxRef];
    if (!type || !ptc) return;

    const key = `${type}_${ptc}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  const result: any[] = [];

  // -------- COMBINE EACH GROUP --------
  Object.values(groups).forEach((group) => {
    const firstItem = group[0];

    const combinedFareDetail = JSON.parse(
      JSON.stringify(firstItem?.farePriceType ?? {}),
    );
    const combinedNetPrice = JSON.parse(
      JSON.stringify(firstItem?.netPrice ?? {}),
    );

    let fareBaseSum = 0;
    let fareTotalSum = 0;
    let fareTaxTotalSum = 0;
    const fareTaxMap: Record<string, any> = {};

    let netBaseSum = 0;
    let netTotalSum = 0;
    let netTaxTotalSum = 0;
    const netTaxMap: Record<string, any> = {};

    let priceDetailSum = 0;

    // -------- SUM --------
    group.forEach((item) => {
      // priceDetail
      if (item?.priceDetail?.cdata !== undefined) {
        priceDetailSum += Number(item?.priceDetail?.cdata ?? 0);
      }

      // ---- farePriceType ----
      const farePrice = item?.farePriceType?.price;
      if (farePrice) {
        fareBaseSum += Number(farePrice?.baseAmount?.cdata ?? 0);
        fareTotalSum += Number(farePrice?.totalAmount?.cdata ?? 0);

        farePrice?.taxSummary?.forEach((ts: any) => {
          fareTaxTotalSum += Number(ts?.totalTaxAmount?.cdata ?? 0);

          ts?.tax?.forEach((t: any) => {
            if (!t?.taxCode) return;

            if (!fareTaxMap[t.taxCode]) {
              fareTaxMap[t.taxCode] = JSON.parse(JSON.stringify(t));
            } else {
              fareTaxMap[t.taxCode].amount.cdata =
                Number(fareTaxMap[t?.taxCode]?.amount?.cdata ?? 0) +
                Number(t.amount?.cdata ?? 0);
            }
          });
        });
      }

      // ---- netPrice ----
      const netPrice = item?.netPrice;
      if (netPrice) {
        netBaseSum += Number(netPrice?.baseAmount?.cdata ?? 0);
        netTotalSum += Number(netPrice?.totalAmount?.cdata ?? 0);

        netPrice?.taxSummary?.forEach((ts: any) => {
          netTaxTotalSum += Number(ts?.totalTaxAmount?.cdata ?? 0);

          ts?.tax?.forEach((t: any) => {
            if (!t?.taxCode) return;

            if (!netTaxMap[t.taxCode]) {
              netTaxMap[t.taxCode] = JSON.parse(JSON.stringify(t));
            } else {
              netTaxMap[t.taxCode].amount.cdata =
                Number(netTaxMap[t.taxCode].amount.cdata ?? 0) +
                Number(t.amount?.cdata ?? 0);
            }
          });
        });
      }
    });

    // farePriceType
    if (combinedFareDetail?.price?.baseAmount?.cdata !== undefined) {
      combinedFareDetail.price.baseAmount.cdata = fareBaseSum.toString();
    }

    if (combinedFareDetail?.price?.totalAmount?.cdata !== undefined) {
      combinedFareDetail.price.totalAmount.cdata = fareTotalSum.toString();
    }

    if (
      combinedFareDetail?.price?.taxSummary?.[0]?.totalTaxAmount?.cdata !==
      undefined
    ) {
      combinedFareDetail.price.taxSummary[0].totalTaxAmount.cdata =
        fareTaxTotalSum.toString();
    }

    if (combinedFareDetail?.price?.taxSummary?.[0]?.tax) {
      combinedFareDetail.price.taxSummary[0].tax = Object.values(fareTaxMap);
    }

    // netPrice
    if (combinedNetPrice?.baseAmount?.cdata !== undefined) {
      combinedNetPrice.baseAmount.cdata = netBaseSum.toString();
    }

    if (combinedNetPrice?.totalAmount?.cdata !== undefined) {
      combinedNetPrice.totalAmount.cdata = netTotalSum.toString();
    }

    if (
      combinedNetPrice?.taxSummary?.[0]?.totalTaxAmount?.cdata !== undefined
    ) {
      combinedNetPrice.taxSummary[0].totalTaxAmount.cdata =
        netTaxTotalSum.toString();
    }

    if (combinedNetPrice?.taxSummary?.[0]?.tax) {
      combinedNetPrice.taxSummary[0].tax = Object.values(netTaxMap);
    }

    const combinedPriceDetail =
      firstItem?.priceDetail?.cdata !== undefined
        ? { ...firstItem.priceDetail, cdata: priceDetailSum }
        : firstItem?.priceDetail;

    result.push({
      ...firstItem,
      passengerRef: Array.from(new Set(group.flatMap((g) => g.passengerRef))),
      priceDetail: combinedPriceDetail,
      farePriceType: combinedFareDetail,
      netPrice: combinedNetPrice,
    });
  });

  return result;
};

const isInternationalTrip = (
  originDestCriteria: any[] | undefined,
  airports: IAirportList[],
) => {
  if (!originDestCriteria?.length || !airports?.length) return false;

  // Build a fast lookup: IATA code (uppercase) → ISO country code (uppercase)
  const countryByCode = new Map<string, string>();
  airports.forEach((ap) => {
    if (ap.airportCode && ap.countryName) {
      countryByCode.set(
        ap.airportCode.toUpperCase(),
        ap.countryName.toUpperCase(),
      );
    }
  });
  return originDestCriteria.some((od) => {
    const originCode = od?.originDepCriteria?.iataLocationCode?.toUpperCase();
    const destCode = od?.destArrivalCriteria?.iataLocationCode?.toUpperCase();

    if (!originCode || !destCode) return false;

    const originCountry = countryByCode.get(originCode);
    const destCountry = countryByCode.get(destCode);

    // If country data is unavailable for either airport, treat conservatively
    // as international (safer to require Passport than to miss a restriction).
    if (!originCountry || !destCountry) return true;

    return originCountry !== destCountry;
  });
};

const getAllowedDocTypes = (
  international: boolean,
  identifiedByConfig: string,
): { allowPassport: boolean; allowNationalId: boolean } => {
  if (international) {
    return { allowPassport: true, allowNationalId: false };
  }

  // Domestic travel: honour the airline config
  const parts = (identifiedByConfig || "").split(":").map((v) => v.trim());

  return {
    allowPassport: parts.includes("PT"),
    allowNationalId: parts.includes("I"),
  };
};

const getAirlineCodes = (flight: any): string[] => {
  if (!flight?.paxSegment?.length) return [];

  const codes = new Set<string>();

  flight.paxSegment.forEach((segment: any) => {
    const marketing = segment?.marketingCarrierInfo?.carrierDesigCode;
    const operating = segment?.operatingCarrierInfo?.carrierDesigCode;

    if (marketing) codes.add(marketing);
    if (operating) codes.add(operating);
  });

  return Array.from(codes);
};

const getTimeDifference = (dateTime: any) => {
  const dtTm = moment(dateTime);
  const now = moment();
  const diffMinutes = dtTm.diff(now, "minutes");
  const diffHours = dtTm.diff(now, "hours");
  const diffDays = dtTm.diff(now, "days");
  return { diffDays, diffHours, diffMinutes };
};

const getActiveSegments = (orderResponse: any) => {
  // returns a list of active segment ids
  let activeSegs: any = [];

  const segList = orderResponse?.dataLists?.paxSegmentList?.paxSegment?.map(
    (seg: any) => seg?.paxSegmentId,
  );
  const flightOrderItems = orderResponse?.order?.[0]?.orderItem?.filter(
    (oi: any) =>
      oi?.airlineTaxonomy?.[0]?.taxonomyCode === FLIGHT_TAXONOMY_CODE,
  );
  segList?.forEach((seg: any) => {
    const isSegActive = flightOrderItems?.some((foi: any) =>
      foi?.service?.some(
        (serv: any) =>
          serv?.orderServiceAssociation?.serviceDefinitionRef
            ?.orderFlightAssociations?.paxSegmentRef?.paxSegmentRefId === seg &&
          serv?.statusCode !== "CANCELLED",
      ),
    );
    if (isSegActive) activeSegs.push(seg);
  });

  return activeSegs;
};

const findPayableOrderItems = (foDetails: any) => {
  let orderItems = foDetails?.response?.order?.[0]?.orderItem;
  if (isEmpty(orderItems)) return [];
  let paidOrderItems: any = [];
  foDetails?.paymentFunctions?.forEach((payment: any) =>
    paidOrderItems.push(...payment?.orderAssociation?.orderItemRefId),
  );
  return (
    orderItems
      .filter(
        (oi: any) =>
          !paidOrderItems?.includes(oi?.orderItemId) &&
          Number(oi?.price?.totalAmount?.cdata) > 0,
      )
      ?.map((oi: any) => oi?.orderItemId) || []
  );
};

export {
  // jwt,
  emptyProxyObject,
  nodeEnv,
  getAllowedDocTypes,
  isInternationalTrip,
  convertCurrencyLocale,
  getEnv,
  verifyAuthToken,
  decodeReadStringifyObj,
  isEmpty,
  setLocalStorage,
  getLocalStorage,
  getSessionStorage,
  clearLocalStorage,
  counterValueHandler,
  dateChangeHandler,
  buildDateToSQLFmt,
  buildDateFromSQLFmt,
  compareObjects,
  convertDurationForUI,
  convertDataUrlToFileBinary,
  deepClone,
  generateRedirectAppUrl,
  resolveRedirectAppUrl,
  transactionIdMapper,
  convertPassengerDetails,
  convertPassengerToMd5,
  getPassengerListByHash,
  getMissedPassengerListByHash,
  getOrderBookedType,
  orderBookedBy,
  setSessionStorage,
  clearSessionStorage,
  ageCalculator,
  carrierFlightCodeAndNumber,
  combineRefactoredRxOfferItems,
  refactorRxOfferItems,
  collectOfferPreBaggageDetails,
  collectOfferPreBaggageDetailsByService,
  // getCalculatedFareAmounts,
  baseFareWithServiceFee,
  getDifferenceInArrivalAndDepartureDays,
  getSupplierPaymentDiscountFees,
  airlineStandardCommission,
  getCurrentIataDetails,
  getPenaltyAmount,
  getTotalFareWithDisServ,
  removeDuplicates,
  isSubset,
  hasCommonElement,
  serviceConfigValue,
  ownerCodeValue,
  ifIncludedRemoveOrAdd,
  constructPaxListRequest,
  getFlownSegmentsFromTicketObject,
  grayScaleCanvas,
  brightnessContrastCanvas,
  sharpenCanvas,
  parseMRZDate,
  convertDataUrlToFile,
  carrierLogoLogic,
  combineFareTypeOrderItemsAndPax,
  getAirlineCodes,
  getPaxSegmentServiceStatus,
  getTimeDifference,
  getActiveSegments,
  findPayableOrderItems
};
